<template>
  <div class="envio-detail-page ld-page">
    <div class="ld-shell">
      <nav class="ld-breadcrumb" aria-label="Ruta de navegación">
        <router-link :to="logisticsHome()"
          ><v-icon small aria-hidden="true">arrow_back</v-icon
          >{{
            isMensajeroShipment ? 'Mensajería' : 'Transportista'
          }}</router-link
        >
        <span aria-hidden="true">/</span><span>Detalle del servicio</span>
      </nav>
      <div v-if="loading" class="ld-loading" role="status">
        <v-progress-circular indeterminate color="#254f87" size="32" /><span
          >Cargando tu servicio…</span
        >
      </div>
      <div v-else-if="!shipment" class="ld-panel ld-empty">
        <v-icon size="40" aria-hidden="true">cloud_off</v-icon>
        <h1>No pudimos cargar el servicio</h1>
        <p>Verifica tu conexión y vuelve a intentarlo.</p>
        <button class="ld-button" type="button" @click="loadShipment">
          Reintentar
        </button>
      </div>
      <template v-else>
        <header class="ld-header">
          <div class="ld-header-title">
            <h1>{{ serviceTitle }}</h1>
            <p>
              <span>{{ shipment.trackingNumber }}</span
              ><span aria-hidden="true">·</span
              ><span>{{ shipment.clientName }}</span>
            </p>
          </div>
          <div class="ld-header-actions">
            <span :class="statusClass(shipment.currentStatus)"
              ><span class="ld-status-dot" aria-hidden="true"></span
              >{{ statusLabel(shipment.currentStatus) }}</span
            ><button type="button" class="ld-map-button" @click="openMap">
              <v-icon small aria-hidden="true">map</v-icon>Ver mapa
            </button>
          </div>
        </header>

        <div class="ld-progress" v-if="bitacora.length">
          <div class="ld-progress-label">
            <span
              ><strong>{{ bitacoraDone }}</strong> de
              {{ bitacora.length }} pasos completados</span
            ><span>{{ progressPercent }}%</span>
          </div>
          <div
            class="ld-progress-track"
            role="progressbar"
            :aria-valuenow="bitacoraDone"
            :aria-valuemax="bitacora.length"
            aria-valuemin="0"
            aria-label="Progreso de la bitácora"
          >
            <span :style="{ width: progressPercent + '%' }"></span>
          </div>
        </div>
        <div class="ld-tabs" role="tablist" aria-label="Contenido del servicio">
          <button
            v-for="tab in detailTabs"
            :id="'tab-' + tab.value"
            :key="tab.value"
            type="button"
            role="tab"
            :aria-selected="String(activeTab === tab.value)"
            :aria-controls="'panel-' + tab.value"
            :tabindex="activeTab === tab.value ? 0 : -1"
            :class="{ 'ld-tab--active': activeTab === tab.value }"
            @click="activeTab = tab.value"
            @keydown="onTabKey($event, tab.value)"
          >
            <v-icon small aria-hidden="true">{{ tab.icon }}</v-icon
            >{{ tab.label
            }}<span
              v-if="tab.value === 'evidencias' && evidenceCount"
              class="ld-tab-count"
              >{{ evidenceCount }}</span
            >
          </button>
        </div>

        <div class="ld-layout">
          <div class="ld-main">
            <section
              id="panel-bitacora"
              v-show="activeTab === 'bitacora'"
              role="tabpanel"
              aria-labelledby="tab-bitacora"
            >
              <div v-if="bitacoraError" class="ld-panel ld-notice" role="alert">
                <v-icon aria-hidden="true">error_outline</v-icon>
                <p>{{ bitacoraError }}</p>
                <button
                  type="button"
                  class="ld-link-button"
                  @click="loadBitacora"
                >
                  Reintentar
                </button>
              </div>
              <div
                v-else-if="bitacoraLoading && !bitacora.length"
                class="ld-panel ld-loading"
                role="status"
              >
                <v-progress-circular
                  indeterminate
                  size="24"
                  color="#254f87"
                />Cargando bitácora…
              </div>
              <step-runner
                v-else-if="bitacora.length && !isManager"
                ref="stepRunner"
                :shipment-id="shipment.id"
                :steps="bitacora"
                @completed="onStepCompleted"
              />
              <section v-else-if="bitacora.length" class="ld-panel ld-overview">
                <v-icon size="30" aria-hidden="true"
                  >assignment_turned_in</v-icon
                >
                <h2>{{ nextStep ? nextStep.title : 'Servicio completado' }}</h2>
                <p v-if="nextStep">
                  El {{ assigneeLabelLower }} asignado completará este paso.
                  Puedes consultar el recorrido y abrir los pasos completados
                  para revisar sus evidencias.
                </p>
                <p v-else>
                  Todos los pasos de la bitácora quedaron registrados.
                </p>
                <div class="ld-overview-meta">
                  <span>{{ bitacoraDone }} / {{ bitacora.length }} pasos</span
                  ><span>{{
                    currentAssignment
                      ? currentAssignment.firstName +
                        ' ' +
                        currentAssignment.lastName
                      : 'Sin responsable asignado'
                  }}</span>
                </div>
              </section>
              <div v-else class="ld-panel ld-empty">
                <v-icon size="36" aria-hidden="true">assignment</v-icon>
                <h2>Este servicio no tiene bitácora</h2>
                <p>
                  {{
                    isManager
                      ? 'Este registro conserva su flujo de estados anterior.'
                      : 'Solicita al administrador que revise la configuración del servicio.'
                  }}
                </p>
              </div>
              <p class="ld-save-note">
                <v-icon small aria-hidden="true">verified_user</v-icon>El avance
                se registra con tu usuario y la fecha de cada paso.
              </p>
            </section>

            <section
              id="panel-evidencias"
              v-show="activeTab === 'evidencias'"
              class="ld-panel ld-section"
              role="tabpanel"
              aria-labelledby="tab-evidencias"
            >
              <div class="ld-section-heading">
                <h2>Evidencias del servicio</h2>
                <span
                  >{{ evidenceCount }}
                  {{ evidenceCount === 1 ? 'registro' : 'registros' }}</span
                >
              </div>
              <div v-if="!evidenceCount" class="ld-empty">
                <v-icon size="36" aria-hidden="true">photo_library</v-icon>
                <h3>Aún no hay evidencias</h3>
                <p>
                  Las fotos y firmas aparecerán aquí cuando se registren en la
                  bitácora.
                </p>
              </div>
              <div v-else class="ld-gallery">
                <figure v-for="ev in photoEvidences" :key="'photo-' + ev.id">
                  <button
                    type="button"
                    :aria-label="'Ampliar ' + (ev.originalName || 'fotografía')"
                    @click="openLightbox(ev)"
                  >
                    <img
                      :src="evidenceUrl(ev)"
                      :alt="ev.originalName || 'Fotografía del servicio'"
                      loading="lazy"
                    /><span class="ld-photo-expand"
                      ><v-icon small aria-hidden="true">zoom_in</v-icon></span
                    >
                  </button>
                  <figcaption>
                    <strong>{{ ev.originalName || 'Fotografía' }}</strong
                    ><span>{{ fmtDateTime(ev.createdAt) }}</span>
                    <p v-if="ev.comment">{{ ev.comment }}</p>
                  </figcaption>
                </figure>
                <figure v-for="st in signedSteps" :key="'signature-' + st.id">
                  <button
                    type="button"
                    :aria-label="
                      'Ver firma de ' + (st.receiverName || 'quien recibe')
                    "
                    @click="openStepDetail(st)"
                  >
                    <img
                      class="ld-signature-image"
                      :src="signatureSrc(stepSignature(st))"
                      alt="Firma de quien recibe"
                      loading="lazy"
                    />
                  </button>
                  <figcaption>
                    <strong>{{
                      st.receiverName || 'Firma de recepción'
                    }}</strong
                    ><span>{{ st.title }}</span>
                  </figcaption>
                </figure>
                <figure
                  v-for="ev in signatureEvidences"
                  :key="'legacy-signature-' + ev.id"
                >
                  <img
                    class="ld-signature-image"
                    :src="signatureSrc(ev.signatureData)"
                    alt="Firma registrada"
                    loading="lazy"
                  />
                  <figcaption>
                    <strong>Firma registrada</strong
                    ><span>{{ fmtDateTime(ev.createdAt) }}</span>
                  </figcaption>
                </figure>
              </div>
            </section>

            <section
              id="panel-informacion"
              v-show="activeTab === 'informacion'"
              class="ld-panel ld-section"
              role="tabpanel"
              aria-labelledby="tab-informacion"
            >
              <div class="ld-section-heading">
                <h2>Información del servicio</h2>
              </div>
              <dl class="ld-info-grid">
                <div v-for="item in serviceDetails" :key="item.label">
                  <dt>{{ item.label }}</dt>
                  <dd>{{ item.value }}</dd>
                </div>
              </dl>
              <div v-if="shipment.observations" class="ld-observations">
                <h3>Observaciones</h3>
                <p>{{ shipment.observations }}</p>
              </div>
              <div class="ld-record">
                <span>Creado {{ fmtDateTime(shipment.createdAt) }}</span
                ><span v-if="shipment.createdByUser"
                  >por {{ shipment.createdByUser.firstName }}
                  {{ shipment.createdByUser.lastName }}</span
                >
              </div>
            </section>

            <section
              id="panel-actividad"
              v-show="activeTab === 'actividad'"
              class="ld-panel ld-section"
              role="tabpanel"
              aria-labelledby="tab-actividad"
            >
              <div class="ld-section-heading">
                <h2>Actividad del servicio</h2>
                <button
                  type="button"
                  class="ld-link-button"
                  :disabled="trackingLoading"
                  @click="loadTracking"
                >
                  <v-icon small aria-hidden="true">refresh</v-icon>Actualizar
                </button>
              </div>
              <p v-if="!tracking.length" class="ld-empty">
                Todavía no hay movimientos registrados.
              </p>
              <ol v-else class="ld-activity">
                <li v-for="t in sortedTracking" :key="t.id">
                  <span class="ld-activity-dot" aria-hidden="true"></span>
                  <div>
                    <strong>{{ statusLabel(t.newStatus) }}</strong>
                    <p v-if="t.comment">{{ t.comment }}</p>
                    <span
                      >{{
                        t.user
                          ? t.user.firstName + ' ' + t.user.lastName + ' · '
                          : ''
                      }}{{ fmtDateTime(t.createdAt) }}</span
                    >
                  </div>
                </li>
              </ol>
            </section>
          </div>

          <aside class="ld-sidebar" aria-label="Resumen del servicio">
            <details
              class="ld-panel ld-steps"
              :open="!$vuetify.breakpoint.smAndDown"
            >
              <summary
                ><span>Recorrido de la bitácora</span
                ><span class="ld-steps-count"
                  >{{ bitacoraDone }}/{{ bitacora.length
                  }}<v-icon small aria-hidden="true">expand_more</v-icon></span
                ></summary
              >
              <ol v-if="bitacora.length" class="ld-step-list">
                <li
                  v-for="(st, i) in bitacora"
                  :key="st.id"
                  :class="{
                    'ld-step--done': isStepDone(st),
                    'ld-step--current': nextStep && nextStep.id === st.id,
                  }"
                >
                  <button
                    type="button"
                    :disabled="
                      !isStepDone(st) && (!nextStep || nextStep.id !== st.id)
                    "
                    :aria-current="
                      nextStep && nextStep.id === st.id ? 'step' : null
                    "
                    @click="visitStep(st)"
                  >
                    <span class="ld-step-marker"
                      ><v-icon v-if="isStepDone(st)" small aria-hidden="true"
                        >check</v-icon
                      ><template v-else>{{ i + 1 }}</template></span
                    >
                    <span class="ld-step-copy"
                      ><strong>{{ st.title }}</strong
                      ><span>{{
                        isStepDone(st)
                          ? 'Completado · ' + fmtDateTime(st.completedAt)
                          : nextStep && nextStep.id === st.id
                          ? 'Paso actual'
                          : 'Pendiente'
                      }}</span></span
                    >
                    <v-icon
                      v-if="isStepDone(st)"
                      small
                      class="ld-step-chevron"
                      aria-hidden="true"
                      >chevron_right</v-icon
                    >
                  </button>
                </li>
              </ol>
              <p v-else class="ld-small-empty">Sin pasos configurados.</p>
            </details>

            <section class="ld-panel ld-route">
              <h2>Ruta del servicio</h2>
              <ol>
                <li
                  v-for="loc in orderedLocations"
                  :key="loc.id || loc.type"
                  :class="{ 'ld-route--destination': loc.type !== 'origin' }"
                >
                  <span class="ld-route-marker" aria-hidden="true"
                    ><v-icon small>{{
                      loc.type === 'origin' ? 'trip_origin' : 'location_on'
                    }}</v-icon></span
                  >
                  <div>
                    <span>{{
                      loc.type === 'origin' ? 'Origen' : 'Destino'
                    }}</span
                    ><strong>{{ loc.address || loc.name }}</strong>
                    <p v-if="loc.contactName">{{ loc.contactName }}</p>
                    <a
                      v-if="loc.contactPhone"
                      :href="'tel:' + loc.contactPhone"
                      >{{ loc.contactPhone }}</a
                    >
                  </div>
                </li>
              </ol>
              <button type="button" class="ld-route-map" @click="openMap">
                <v-icon small aria-hidden="true">near_me</v-icon>Ver ruta en el
                mapa<v-icon small aria-hidden="true">open_in_new</v-icon>
              </button>
            </section>

            <section class="ld-panel ld-assignee">
              <h2>{{ assigneeLabel }} asignado</h2>
              <div class="ld-person">
                <span class="ld-avatar">{{ assigneeInitials }}</span>
                <div>
                  <strong>{{
                    currentAssignment
                      ? currentAssignment.firstName +
                        ' ' +
                        currentAssignment.lastName
                      : 'Sin asignar'
                  }}</strong
                  ><span>{{
                    isMensajeroShipment ? 'Mensajería' : 'Transporte de carga'
                  }}</span>
                </div>
                <span v-if="liveTracking" class="ld-live">En vivo</span>
              </div>
              <button
                v-if="
                  isManager &&
                    !isInTransit &&
                    !bitacoraDone &&
                    shipment.currentStatus !== 'entregado'
                "
                type="button"
                class="ld-link-button"
                @click="openAssignDialog"
              >
                {{
                  currentAssignment
                    ? 'Cambiar responsable'
                    : 'Asignar responsable'
                }}
              </button>
            </section>

            <div v-if="isManager && !bitacora.length" class="ld-legacy-actions">
              <button
                v-if="isAsignado"
                class="ld-button"
                type="button"
                :disabled="startingTrip"
                @click="startTrip"
              >
                Iniciar viaje</button
              ><button
                class="ld-link-button"
                type="button"
                @click="statusDialog.show = true"
              >
                Cambiar estado
              </button>
            </div>
          </aside>
        </div>
      </template>
    </div>

    <!-- Dialog: Cambiar Estado -->
    <v-dialog max-width="480" v-model="statusDialog.show" persistent>
      <v-card class="modal-card">
        <div class="modal-header">
          <v-icon class="modal-header__icon">swap_horiz</v-icon>
          <span class="modal-header__title">Cambiar Estado</span>
        </div>
        <div class="modal-body">
          <div class="form-field">
            <label class="form-label"
              >Nuevo Estado <span class="form-required">*</span></label
            >
            <v-select
              v-model="statusDialog.status"
              :items="statusOptions"
              solo
              class="form-input"
              hide-details
              placeholder="Selecciona un estado"
            ></v-select>
          </div>
          <div class="form-field" style="margin-top:12px">
            <label class="form-label">Observaciones</label>
            <v-textarea
              v-model="statusDialog.observations"
              solo
              rows="3"
              class="form-input"
              placeholder="Comentario sobre el cambio de estado..."
            ></v-textarea>
          </div>
        </div>
        <div class="modal-footer">
          <v-btn
            class="btn-cancel"
            flat
            @click="statusDialog.show = false"
            :disabled="statusDialog.saving"
            >Cancelar</v-btn
          >
          <v-btn
            class="btn-save"
            :loading="statusDialog.saving"
            :disabled="!statusDialog.status"
            @click="submitStatus"
          >
            Guardar
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Dialog: Asignar Mensajero -->
    <v-dialog max-width="460" v-model="assignDialog.show" persistent>
      <v-card class="modal-card">
        <div class="modal-header">
          <v-icon class="modal-header__icon">person_pin_circle</v-icon>
          <span class="modal-header__title">Asignar {{ assigneeLabel }}</span>
        </div>
        <div class="modal-body">
          <div v-if="assignDialog.loading" class="assign-loading">
            <v-progress-circular
              indeterminate
              color="#254F87"
              size="28"
            ></v-progress-circular>
            <span>Cargando {{ assigneeLabelLower }}s...</span>
          </div>
          <template v-else>
            <div class="form-field">
              <label class="form-label"
                >{{ assigneeLabel }} <span class="form-required">*</span></label
              >
              <v-select
                v-model="assignDialog.courierId"
                :items="courierItems"
                solo
                class="form-input"
                hide-details
                :placeholder="`Selecciona un ${assigneeLabelLower}`"
                :no-data-text="`Sin ${assigneeLabelLower}s disponibles`"
              ></v-select>
            </div>
            <div class="form-field" style="margin-top:12px">
              <label class="form-label">Comentario</label>
              <v-textarea
                v-model="assignDialog.comment"
                solo
                rows="2"
                class="form-input"
                placeholder="Comentario sobre la asignación (opcional)..."
              ></v-textarea>
            </div>
            <p
              v-if="!assignDialog.users.length && !assignDialog.loading"
              class="assign-empty"
            >
              No hay {{ assigneeLabelLower }}s disponibles.
            </p>
          </template>
        </div>
        <div class="modal-footer">
          <v-btn
            class="btn-cancel"
            flat
            @click="assignDialog.show = false"
            :disabled="assignDialog.saving"
            >Cancelar</v-btn
          >
          <v-btn
            class="btn-save"
            :loading="assignDialog.saving"
            :disabled="!assignDialog.courierId"
            @click="submitAssign"
          >
            Asignar
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Dialog: Mapa / Seguimiento -->
    <v-dialog max-width="900" v-model="mapDialog.show" persistent>
      <v-card class="modal-card">
        <div class="modal-header modal-header--map">
          <v-icon class="modal-header__icon">directions</v-icon>
          <span class="modal-header__title">Seguimiento del Servicio</span>
          <span v-if="liveTracking" class="live-badge">
            <span class="live-dot"></span> EN VIVO
          </span>
          <v-btn icon class="ml-auto map-close-btn" @click="closeMap" dark>
            <v-icon>close</v-icon>
          </v-btn>
        </div>
        <div class="map-dialog-body">
          <div v-if="liveTracking" class="live-banner">
            <v-icon small class="live-banner__icon">my_location</v-icon>
            <span v-if="liveLocation">
              Ubicación del repartidor en tiempo real ·
              {{ liveLocation.lat.toFixed(5) }},
              {{ liveLocation.lng.toFixed(5) }}
            </span>
            <span v-else>Esperando ubicación del repartidor…</span>
          </div>
          <div class="map-legend" v-if="mapDialog.origin || mapDialog.dest">
            <div
              class="map-legend-item map-legend-item--origin"
              v-if="mapDialog.origin"
            >
              <v-icon small>trip_origin</v-icon>
              <span>{{ mapDialog.origin }}</span>
            </div>
            <div
              class="map-legend-item map-legend-item--dest"
              v-if="mapDialog.dest"
            >
              <v-icon small>place</v-icon>
              <span>{{ mapDialog.dest }}</span>
            </div>
          </div>
          <div class="map-overlay" v-if="mapDialog.loading">
            <v-progress-circular
              indeterminate
              color="#254F87"
              size="40"
            ></v-progress-circular>
            <span>Calculando ruta...</span>
          </div>
          <div class="map-error-banner" v-if="mapDialog.error">
            <v-icon small>error_outline</v-icon>
            {{ mapDialog.error }}
          </div>
          <GmapMap
            ref="gmap"
            :center="mapDialog.center"
            :zoom="mapDialog.zoom"
            style="width:100%;height:480px"
            :options="{
              mapTypeControl: false,
              fullscreenControl: true,
              streetViewControl: false,
              gestureHandling: 'greedy',
              zoomControlOptions: { position: 3 },
            }"
          ></GmapMap>
        </div>
      </v-card>
    </v-dialog>

    <!-- Dialog: Historial de Tracking -->
    <v-dialog max-width="580" v-model="trackingDialog.show" scrollable>
      <v-card class="modal-card">
        <div class="modal-header">
          <v-icon class="modal-header__icon">timeline</v-icon>
          <span class="modal-header__title">Historial de Tracking</span>
          <v-btn
            icon
            small
            :loading="trackingLoading"
            @click="loadTracking"
            style="margin-left:auto"
          >
            <v-icon small>refresh</v-icon>
          </v-btn>
          <v-btn icon small @click="trackingDialog.show = false">
            <v-icon small>close</v-icon>
          </v-btn>
        </div>
        <v-card-text style="padding:0;max-height:520px;overflow-y:auto">
          <div
            v-if="trackingLoading && !tracking.length"
            class="empty-section"
            style="padding:40px 20px"
          >
            <v-progress-circular
              indeterminate
              color="#254F87"
              size="28"
            ></v-progress-circular>
          </div>
          <div
            v-else-if="!tracking.length"
            class="empty-section"
            style="padding:40px 20px"
          >
            <v-icon class="empty-section__icon">history</v-icon>
            <p class="empty-section__text">Sin historial de tracking</p>
          </div>
          <div v-else class="timeline" style="padding:18px 20px">
            <div
              v-for="(t, i) in sortedTracking"
              :key="t.id"
              class="timeline-item"
            >
              <div class="tl-rail">
                <div class="tl-dot" :style="dotStyle(t.newStatus)"></div>
                <div class="tl-line" v-if="i < sortedTracking.length - 1"></div>
              </div>
              <div class="tl-body">
                <div class="tl-transition">
                  <span v-if="t.previousStatus" class="tl-prev">{{
                    statusLabel(t.previousStatus)
                  }}</span>
                  <v-icon v-if="t.previousStatus" x-small class="tl-arrow"
                    >arrow_forward</v-icon
                  >
                  <span
                    :class="statusClass(t.newStatus)"
                    style="font-size:11px"
                    >{{ statusLabel(t.newStatus) }}</span
                  >
                </div>
                <div class="tl-meta">
                  <span class="tl-user" v-if="t.user">
                    <span class="tl-avatar">{{
                      (t.user.firstName[0] + t.user.lastName[0]).toUpperCase()
                    }}</span>
                    {{ t.user.firstName }} {{ t.user.lastName }}
                  </span>
                  <span class="tl-date">{{ fmtDateTime(t.createdAt) }}</span>
                </div>
                <p class="tl-comment" v-if="t.comment">{{ t.comment }}</p>
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Lightbox: Evidencia -->
    <v-dialog max-width="800" v-model="lightbox.show">
      <v-card class="modal-card">
        <div class="modal-header">
          <v-icon class="modal-header__icon">photo_camera</v-icon>
          <span class="modal-header__title">{{
            lightbox.alt || 'Evidencia'
          }}</span>
          <v-btn icon class="ml-auto" @click="lightbox.show = false">
            <v-icon>close</v-icon>
          </v-btn>
        </div>
        <div class="lightbox-body">
          <img :src="lightbox.src" :alt="lightbox.alt" class="lightbox-img" />
        </div>
      </v-card>
    </v-dialog>

    <!-- Dialog: Detalle del paso de bitácora -->
    <v-dialog max-width="640" v-model="stepDialog.show" scrollable>
      <v-card class="modal-card" v-if="stepDialog.step">
        <div class="modal-header">
          <v-icon class="modal-header__icon">fact_check</v-icon>
          <span class="modal-header__title">{{ stepDialog.step.title }}</span>
          <v-btn icon class="ml-auto" @click="stepDialog.show = false">
            <v-icon>close</v-icon>
          </v-btn>
        </div>
        <v-card-text class="step-detail-body">
          <p
            v-if="isStepDone(stepDialog.step)"
            class="bita-meta"
            style="margin: 0 0 16px"
          >
            <v-icon x-small>check_circle</v-icon>
            {{ stepCompletedBy(stepDialog.step) }}
            <template v-if="stepDialog.step.completedAt"
              >· {{ fmtDateTime(stepDialog.step.completedAt) }}</template
            >
          </p>

          <!-- Comentario -->
          <div v-if="stepDialog.step.comment" class="step-block">
            <p class="step-block__label">
              <v-icon x-small>chat_bubble_outline</v-icon> Comentario
            </p>
            <p class="step-block__text">{{ stepDialog.step.comment }}</p>
          </div>

          <!-- Receptor -->
          <div v-if="stepDialog.step.receiverName" class="step-block">
            <p class="step-block__label">
              <v-icon x-small>person</v-icon> Recibió
            </p>
            <p class="step-block__text">{{ stepDialog.step.receiverName }}</p>
          </div>

          <!-- Fotos -->
          <div v-if="stepPhotos(stepDialog.step).length" class="step-block">
            <p class="step-block__label">
              <v-icon x-small>photo_camera</v-icon> Fotografías ({{
                stepPhotos(stepDialog.step).length
              }})
            </p>
            <div class="ev-photo-grid">
              <div
                v-for="ev in stepPhotos(stepDialog.step)"
                :key="ev.id"
                class="ev-photo-item"
                @click="openLightbox(ev)"
              >
                <img
                  :src="evidenceUrl(ev)"
                  :alt="ev.originalName"
                  class="ev-photo-img"
                />
                <div class="ev-photo-overlay">
                  <v-icon small color="white">zoom_in</v-icon>
                </div>
                <div class="ev-photo-foot">
                  <span v-if="ev.latitude != null">
                    <v-icon x-small>place</v-icon>
                    {{ Number(ev.latitude).toFixed(4) }},
                    {{ Number(ev.longitude).toFixed(4) }}
                  </span>
                  <span>{{ fmtDateTime(ev.createdAt) }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Firma -->
          <div v-if="stepSignature(stepDialog.step)" class="step-block">
            <p class="step-block__label">
              <v-icon x-small>gesture</v-icon> Firma
            </p>
            <div class="step-signature">
              <img
                :src="signatureSrc(stepSignature(stepDialog.step))"
                alt="Firma digital"
                class="step-signature__img"
              />
            </div>
          </div>

          <!-- Ubicación -->
          <div v-if="stepGps(stepDialog.step)" class="step-block">
            <p class="step-block__label">
              <v-icon x-small>place</v-icon> Ubicación
            </p>
            <a
              class="step-gps-link"
              :href="gpsLink(stepGps(stepDialog.step))"
              target="_blank"
              rel="noopener"
            >
              <v-icon x-small>near_me</v-icon>
              {{ Number(stepGps(stepDialog.step).lat).toFixed(5) }},
              {{ Number(stepGps(stepDialog.step).lng).toFixed(5) }}
            </a>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snack.show"
      :timeout="4000"
      :color="snack.color"
      top
      right
    >
      {{ snack.message }}
      <v-btn flat dark @click="snack.show = false">Cerrar</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import Api from '@/api/index'
import AppConfig from '@/plugins/config/AppConfig'
import StepRunner from '@/components/logistics/StepRunner.vue'
import { logisticsManager, logisticsHome } from '@/helpers/Logistics'
import SessionHelper from '@/helpers/SessionHelper'

const STATUS_MAP = {
  pendiente: { label: 'Pendiente', cls: 'status-badge status-badge--pending' },
  asignado: { label: 'Asignado', cls: 'status-badge status-badge--assigned' },
  en_transito: {
    label: 'En tránsito',
    cls: 'status-badge status-badge--transit',
  },
  llegada: { label: 'Llegada', cls: 'status-badge status-badge--llegada' },
  evidencia: {
    label: 'Evidencia',
    cls: 'status-badge status-badge--evidencia',
  },
  firma: { label: 'Firma', cls: 'status-badge status-badge--firma' },
  entregado: {
    label: 'Entregado',
    cls: 'status-badge status-badge--delivered',
  },
}

const PRIORITY_MAP = {
  baja: { label: 'Baja', cls: 'priority-badge priority-badge--normal' },
  media: { label: 'Media', cls: 'priority-badge priority-badge--high' },
  alta: { label: 'Alta', cls: 'priority-badge priority-badge--urgent' },
}

export default {
  name: 'EnvioDetail',
  components: { StepRunner },
  data() {
    return {
      activeTab: 'bitacora',
      detailTabs: [
        { value: 'bitacora', label: 'Bitácora', icon: 'assignment' },
        { value: 'evidencias', label: 'Evidencias', icon: 'photo_library' },
        { value: 'informacion', label: 'Información', icon: 'info_outline' },
        { value: 'actividad', label: 'Actividad', icon: 'history' },
      ],
      loading: true,
      error: false,
      shipment: null,
      tracking: [],
      trackingLoading: false,
      bitacora: [],
      bitacoraLoading: false,
      bitacoraError: '',
      startingTrip: false,
      liveTracking: false,
      liveLocation: null,
      statusDialog: {
        show: false,
        status: null,
        observations: '',
        saving: false,
      },
      snack: { show: false, color: '', message: '' },
      rolesCatalog: [],
      trackingDialog: { show: false },
      lightbox: { show: false, src: '', alt: '' },
      stepDialog: { show: false, step: null },
      assignDialog: {
        show: false,
        loading: false,
        courierId: null,
        comment: '',
        saving: false,
        users: [],
      },
      mapDialog: {
        show: false,
        loading: false,
        error: '',
        center: { lat: 8.9936, lng: -79.5197 },
        zoom: 10,
        origin: '',
        dest: '',
      },
      statusOptions: [
        { text: 'Pendiente', value: 'pendiente' },
        { text: 'Asignado', value: 'asignado' },
        { text: 'En tránsito', value: 'en_transito' },
        { text: 'Llegada', value: 'llegada' },
        { text: 'Evidencia', value: 'evidencia' },
        { text: 'Firma', value: 'firma' },
        { text: 'Entregado', value: 'entregado' },
      ],
    }
  },
  beforeDestroy() {
    if (this._gpsWatch != null) navigator.geolocation.clearWatch(this._gpsWatch)
  },
  watch: {
    isInTransit(active) {
      if (
        active &&
        !this.isManager &&
        navigator.geolocation &&
        this._gpsWatch == null
      ) {
        this._gpsWatch = navigator.geolocation.watchPosition(
          p => {
            if (this._lastPing && Date.now() - this._lastPing < 15000) return
            this._lastPing = Date.now()
            Api.Shipment.liveLocation(this.$route.params.id, {
              latitude: p.coords.latitude,
              longitude: p.coords.longitude,
            }).catch(() => {})
          },
          () => {},
          { enableHighAccuracy: true, maximumAge: 15000 }
        )
      } else if (!active && this._gpsWatch != null) {
        navigator.geolocation.clearWatch(this._gpsWatch)
        this._gpsWatch = null
      }
    },
  },
  created() {
    this.loadShipment()
    // Unirse a la room del servicio específico para recibir actualizaciones en tiempo real
    if (this.$socket) {
      this.$socket.emit('join_shipment_room', {
        shipmentId: this.$route.params.id,
        token: SessionHelper.token,
      })
    }
  },
  computed: {
    serviceTitle() {
      const name =
        (this.shipment &&
          (this.shipment.bitacoraTemplateName ||
            (this.isMensajeroShipment
              ? 'Servicio de mensajería'
              : 'Entrega de carga'))) ||
        'Servicio de logística'
      return name.replace(/^Bitácora (de |para )?/i, '')
    },
    nextStep() {
      return this.bitacora.find(st => !this.isStepDone(st))
    },
    progressPercent() {
      return this.bitacora.length
        ? Math.round((this.bitacoraDone / this.bitacora.length) * 100)
        : 0
    },
    signedSteps() {
      return this.bitacora.filter(st => this.stepSignature(st))
    },
    evidenceCount() {
      return (
        this.photoEvidences.length +
        this.signedSteps.length +
        this.signatureEvidences.length
      )
    },
    orderedLocations() {
      return ((this.shipment && this.shipment.locations) || [])
        .slice()
        .sort(
          (a, b) =>
            (a.type === 'origin' ? 0 : 1) - (b.type === 'origin' ? 0 : 1)
        )
    },
    assigneeInitials() {
      return this.currentAssignment
        ? (
            (this.currentAssignment.firstName || '').charAt(0) +
            (this.currentAssignment.lastName || '').charAt(0)
          ).toUpperCase()
        : '—'
    },
    serviceDetails() {
      const s = this.shipment || {}
      return [
        { label: 'Cliente', value: s.clientName },
        { label: 'Teléfono', value: s.clientPhone },
        { label: 'Correo electrónico', value: s.clientEmail },
        { label: 'Número de seguimiento', value: s.trackingNumber },
        { label: 'Referencia', value: s.referenceNumber },
        { label: 'Factura', value: s.invoiceNumber },
        { label: 'Descripción', value: s.description },
        { label: 'Fecha de entrada', value: this.fmtDate(s.entryDate) },
        {
          label: 'Entrega estimada',
          value: s.estimatedDeliveryDate
            ? this.fmtDate(s.estimatedDeliveryDate)
            : null,
        },
        { label: 'Prioridad', value: this.priorityLabel(s.priority) },
        { label: 'Peso', value: s.weight ? s.weight + ' kg' : null },
        { label: 'Volumen', value: s.volume ? s.volume + ' m³' : null },
      ].filter(item => item.value)
    },
    isManager: logisticsManager,
    isInTransit() {
      return (
        this.shipment &&
        (this.shipment.currentStatus || '').toLowerCase() === 'en_transito'
      )
    },
    isAsignado() {
      return (
        this.shipment &&
        (this.shipment.currentStatus || '').toLowerCase() === 'asignado'
      )
    },
    bitacoraDone() {
      return this.bitacora.filter(st => this.isStepDone(st)).length
    },
    isMensajeroShipment() {
      return (
        this.shipment &&
        (this.shipment.assignmentType || 'transportista').toLowerCase() ===
          'mensajero'
      )
    },
    assigneeLabel() {
      return this.isMensajeroShipment ? 'Mensajero' : 'Transportista'
    },
    assigneeLabelLower() {
      return this.isMensajeroShipment ? 'mensajero' : 'transportista'
    },
    currentAssignment() {
      if (!this.shipment) return null
      // El asignado activo viene directo en assignedUser; si no, del histórico
      if (this.shipment.assignedUser) return this.shipment.assignedUser
      if (this.shipment.assignments && this.shipment.assignments.length) {
        const last = this.shipment.assignments[
          this.shipment.assignments.length - 1
        ]
        return last.assignedUser || last.user || last.courier || null
      }
      return null
    },
    courierItems() {
      return this.assignDialog.users.map(u => ({
        text: `${u.firstName} ${u.lastName}`,
        value: u.id,
      }))
    },
    photoEvidences() {
      if (!this.shipment || !this.shipment.evidences) return []
      return this.shipment.evidences.filter(
        e =>
          e.type === 'photo' || (e.mimeType && e.mimeType.startsWith('image'))
      )
    },
    signatureEvidences() {
      if (!this.shipment || !this.shipment.evidences) return []
      return this.shipment.evidences.filter(
        e => e.signatureData && !e.bitacoraStepId
      )
    },
    sortedTracking() {
      return [...this.tracking].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      )
    },
  },
  methods: {
    onTabKey(event, value) {
      const index = this.detailTabs.findIndex(tab => tab.value === value)
      const movements = { ArrowRight: 1, ArrowLeft: -1 }
      if (!(event.key in movements) && !['Home', 'End'].includes(event.key))
        return
      event.preventDefault()
      const next =
        event.key === 'Home'
          ? 0
          : event.key === 'End'
          ? this.detailTabs.length - 1
          : (index + movements[event.key] + this.detailTabs.length) %
            this.detailTabs.length
      this.activeTab = this.detailTabs[next].value
      this.$nextTick(() =>
        document.getElementById('tab-' + this.activeTab).focus()
      )
    },
    visitStep(step) {
      if (this.isStepDone(step)) this.openStepDetail(step)
      else {
        this.activeTab = 'bitacora'
        this.$nextTick(() => {
          if (this.$refs.stepRunner)
            this.$refs.stepRunner.$el.scrollIntoView({
              block: 'start',
              behavior: window.matchMedia('(prefers-reduced-motion: reduce)')
                .matches
                ? 'auto'
                : 'smooth',
            })
        })
      }
    },
    logisticsHome() {
      return this.shipment && this.shipment.assignmentType === 'transportista'
        ? '/logistica/transportista'
        : logisticsHome()
    },
    onStepCompleted(steps) {
      this.bitacora = steps
      this.refreshShipment()
      this.$nextTick(() => {
        const runner = this.$refs.stepRunner
        const heading = runner && runner.$el.querySelector('h2')
        if (heading) {
          heading.focus({ preventScroll: true })
          runner.$el.scrollIntoView({
            block: 'start',
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)')
              .matches
              ? 'auto'
              : 'smooth',
          })
        }
      })
    },
    async refreshShipment() {
      const res = await this.getById(this.$route.params.id)
      if (res && res.status < 400) {
        this.shipment = res.data
        this.tracking = res.data.tracking || []
      }
    },
    ...mapActions('shipments', [
      'getById',
      'changeStatus',
      'getTracking',
      'assignMessenger',
      'getBitacora',
    ]),
    isThisShipment(data) {
      return !!(
        data &&
        this.shipment &&
        String(data.shipmentId) === String(this.shipment.id)
      )
    },
    async loadShipment() {
      this.loading = true
      this.error = false
      try {
        const res = await this.getById(this.$route.params.id)
        if (res && res.status < 400 && res.data) {
          this.shipment = res.data
          this.tracking = res.data.tracking || []
          // Si ya está en tránsito, mostrar indicador en vivo
          this.liveTracking = this.isInTransit
          this.loadBitacora()
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },
    async loadBitacora() {
      this.bitacoraLoading = true
      this.bitacoraError = ''
      try {
        const res = await this.getBitacora(this.$route.params.id)
        if (!res || res.status >= 400)
          throw new Error('No se pudo cargar la bitácora. Reintente.')
        const data = res && res.data ? res.data : res
        const list = Array.isArray(data)
          ? data
          : (data && (data.steps || data.items || data.bitacora)) || []
        this.bitacora = list
      } catch (e) {
        this.bitacoraError = e.message
      } finally {
        this.bitacoraLoading = false
      }
    },
    isStepDone(st) {
      if (!st) return false
      const status = (st.status || '').toLowerCase()
      return (
        status === 'completed' || status === 'completado' || !!st.completedAt
      )
    },
    stepCompletedBy(st) {
      const u = st && (st.completedByUser || st.user)
      if (!u) return 'Completado'
      return `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Completado'
    },
    async startTrip() {
      this.startingTrip = true
      try {
        const res = await this.$store.dispatch('shipments/startTrip', {
          id: this.$route.params.id,
          payload: {},
        })
        if (res && res.status >= 200 && res.status < 300) {
          this.liveTracking = true
          this.$toast.success('Viaje iniciado. Seguimiento en vivo activo.')
          this.loadShipment()
        } else {
          const msg =
            (res && res.data && res.data.message) ||
            'No se pudo iniciar el viaje.'
          this.$toast.error(msg)
        }
      } catch (e) {
        this.$toast.error('Error inesperado al iniciar el viaje.')
      } finally {
        this.startingTrip = false
      }
    },
    async loadTracking() {
      this.trackingLoading = true
      try {
        const res = await this.getTracking(this.$route.params.id)
        this.tracking =
          res && res.data
            ? Array.isArray(res.data)
              ? res.data
              : res.data.items || []
            : []
      } catch (e) {
        /* silent */
      } finally {
        this.trackingLoading = false
      }
    },
    async submitStatus() {
      if (!this.statusDialog.status) return
      this.statusDialog.saving = true
      try {
        const res = await this.changeStatus({
          id: this.$route.params.id,
          status: this.statusDialog.status,
          comment: this.statusDialog.observations,
        })
        if (res && res.status >= 200 && res.status < 300) {
          this.$set(this.shipment, 'currentStatus', this.statusDialog.status)
          this.$toast.success('Estado actualizado correctamente.')
          this.statusDialog.show = false
          this.statusDialog.status = null
          this.statusDialog.observations = ''
          this.loadTracking()
        } else {
          const msg =
            (res && res.data && res.data.message) ||
            'No se pudo actualizar el estado.'
          this.$toast.error(msg)
        }
      } catch (e) {
        this.$toast.error('Error inesperado al cambiar estado.')
      } finally {
        this.statusDialog.saving = false
      }
    },
    statusClass(s) {
      return (
        STATUS_MAP[(s || '').toLowerCase()] || {
          cls: 'status-badge status-badge--default',
        }
      ).cls
    },
    statusLabel(s) {
      return (STATUS_MAP[(s || '').toLowerCase()] || { label: s || '—' }).label
    },
    priorityClass(p) {
      return (
        PRIORITY_MAP[(p || '').toLowerCase()] || {
          cls: 'priority-badge priority-badge--normal',
        }
      ).cls
    },
    priorityLabel(p) {
      return (PRIORITY_MAP[(p || '').toLowerCase()] || { label: p || '—' })
        .label
    },
    fmtDate(val) {
      if (!val) return '—'
      return new Date(val).toLocaleDateString('es-PA', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    },
    fmtDateTime(val) {
      if (!val) return '—'
      return new Date(val).toLocaleString('es-PA', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    evidenceUrl(ev) {
      if (!ev || !ev.filePath) return ''
      const config = AppConfig.load()
      return (config.BASE_URL || '') + ev.filePath
    },
    dotStyle(status) {
      const colors = {
        pendiente: '#92711b',
        asignado: '#254F87',
        en_transito: '#C79A2B',
        llegada: '#7e22ce',
        evidencia: '#854d0e',
        firma: '#0369a1',
        entregado: '#2a6d3e',
      }
      const c = colors[(status || '').toLowerCase()] || '#94a3b8'
      return { background: c, borderColor: c, boxShadow: `0 0 0 3px ${c}33` }
    },

    // ── Assign messenger / carrier ────────────────────────────
    async resolveRoleId({ code, nameHint, fallback }) {
      if (!this.rolesCatalog.length) {
        const res = await Api.User.getRoles()
        let list = []
        if (Array.isArray(res)) list = res
        else if (res && Array.isArray(res.items)) list = res.items
        else if (res && res.data)
          list = Array.isArray(res.data) ? res.data : res.data.items || []
        this.rolesCatalog = list
      }
      const up = s => (s || '').toString().toUpperCase()
      // Match por code (estable entre entornos); nombre como respaldo
      const match = this.rolesCatalog.find(
        r => up(r.code) === code || (nameHint && up(r.name).includes(nameHint))
      )
      return match ? match.id : fallback
    },
    async openAssignDialog() {
      this.assignDialog.show = true
      if (this.assignDialog.users.length) return
      this.assignDialog.loading = true
      try {
        // El rol a traer depende del tipo de servicio
        const cfg = this.isMensajeroShipment
          ? { code: 'MENSAJERO', nameHint: 'MENSAJER', fallback: null }
          : { code: 'TRA', nameHint: 'TRANSPORT', fallback: null }
        const roleId = await this.resolveRoleId(cfg)
        const params = { rpp: 100, page: 1 }
        if (roleId != null) params.roleId = roleId
        const res = await Api.User.getAll(params)
        const raw = res
          ? Array.isArray(res)
            ? res
            : res.items || res.data || []
          : []
        this.assignDialog.users = raw
      } catch (e) {
        /* silent */
      } finally {
        this.assignDialog.loading = false
      }
    },
    async submitAssign() {
      if (!this.assignDialog.courierId) return
      this.assignDialog.saving = true
      try {
        const res = await this.assignMessenger({
          id: this.$route.params.id,
          assignedUserId: this.assignDialog.courierId,
          comment: this.assignDialog.comment,
        })
        if (res && res.status >= 200 && res.status < 300) {
          this.$toast.success(`${this.assigneeLabel} asignado correctamente.`)
          this.assignDialog.show = false
          this.assignDialog.courierId = null
          this.assignDialog.comment = ''
          this.loadShipment()
        } else {
          const msg =
            (res && res.data && res.data.message) ||
            'No se pudo asignar el mensajero.'
          this.$toast.error(msg)
        }
      } catch (e) {
        this.$toast.error('Error inesperado al asignar mensajero.')
      } finally {
        this.assignDialog.saving = false
      }
    },

    // ── Map / seguimiento ─────────────────────────────────────
    async openMap() {
      this.mapDialog.show = true
      this.mapDialog.loading = true
      this.mapDialog.error = ''

      // Clean up previous renderer if exists
      if (this._directionsRenderer) {
        this._directionsRenderer.setMap(null)
        this._directionsRenderer = null
      }

      const locations = (this.shipment && this.shipment.locations) || []
      const originLoc = locations.find(l => l.type === 'origin')
      const destLoc = locations.find(
        l => l.type === 'destination' || l.type === 'dest'
      )

      this.mapDialog.origin = originLoc
        ? originLoc.address || ''
        : this.shipment.origin || ''
      this.mapDialog.dest = destLoc
        ? destLoc.address || ''
        : this.shipment.destination || ''

      // Give Vuetify dialog animation time to finish before GmapMap mounts
      await this.$nextTick()
      await this.$nextTick()
      await new Promise(resolve => setTimeout(resolve, 350))

      try {
        const mapComponent = this.$refs.gmap
        if (!mapComponent) {
          this.mapDialog.error = 'No se pudo inicializar el mapa.'
          this.mapDialog.loading = false
          return
        }

        const mapObj = await mapComponent.$mapPromise
        const google = window.google
        this._mapObj = mapObj
        this._google = google
        // Si ya hay una ubicación en vivo conocida, dibújala
        this.applyLiveMarker()

        // Determine origin/dest: prefer lat/lng coords, fall back to address string
        const originPt =
          originLoc && originLoc.latitude != null
            ? {
                lat: parseFloat(originLoc.latitude),
                lng: parseFloat(originLoc.longitude),
              }
            : this.mapDialog.origin || null
        const destPt =
          destLoc && destLoc.latitude != null
            ? {
                lat: parseFloat(destLoc.latitude),
                lng: parseFloat(destLoc.longitude),
              }
            : this.mapDialog.dest || null

        if (!originPt || !destPt) {
          this.mapDialog.error =
            'No se encontraron coordenadas de origen o destino.'
          this.mapDialog.loading = false
          return
        }

        const dr = new google.maps.DirectionsRenderer({
          map: mapObj,
          suppressMarkers: false,
          polylineOptions: {
            strokeColor: '#254F87',
            strokeWeight: 5,
            strokeOpacity: 0.85,
          },
        })
        this._directionsRenderer = dr

        const ds = new google.maps.DirectionsService()
        ds.route(
          {
            origin: originPt,
            destination: destPt,
            travelMode: google.maps.TravelMode.DRIVING,
          },
          (result, status) => {
            this.mapDialog.loading = false
            if (status === 'OK') {
              dr.setDirections(result)
            } else {
              this.mapDialog.error = `No se pudo trazar la ruta (${status}).`
              // Place manual markers so at least origin/dest are visible
              this._placeManualMarkers(mapObj, google, originPt, destPt)
            }
          }
        )
      } catch (e) {
        this.mapDialog.loading = false
        this.mapDialog.error = 'Error al cargar el mapa.'
      }
    },
    _placeManualMarkers(mapObj, google, originPt, destPt) {
      const bounds = new google.maps.LatLngBounds()
      ;[
        { pos: originPt, label: 'A', title: 'Origen' },
        { pos: destPt, label: 'B', title: 'Destino' },
      ].forEach(({ pos, label, title }) => {
        const ll =
          typeof pos === 'string'
            ? null
            : new google.maps.LatLng(pos.lat, pos.lng)
        if (!ll) return
        new google.maps.Marker({ position: ll, map: mapObj, label, title })
        bounds.extend(ll)
      })
      mapObj.fitBounds(bounds)
    },
    closeMap() {
      this.mapDialog.show = false
      this.mapDialog.error = ''
      this.mapDialog.loading = false
      if (this._directionsRenderer) {
        this._directionsRenderer.setMap(null)
        this._directionsRenderer = null
      }
      if (this._liveMarker) {
        this._liveMarker.setMap(null)
        this._liveMarker = null
      }
      this._mapObj = null
      this._google = null
    },
    // ── Marcador en vivo (GPS del repartidor) ─────────────────
    applyLiveMarker() {
      if (!this._mapObj || !this._google || !this.liveLocation) return
      const g = this._google
      const pos = new g.maps.LatLng(
        this.liveLocation.lat,
        this.liveLocation.lng
      )
      if (this._liveMarker) {
        this._liveMarker.setPosition(pos)
      } else {
        this._liveMarker = new g.maps.Marker({
          position: pos,
          map: this._mapObj,
          title: 'Ubicación en vivo',
          zIndex: 9999,
          icon: {
            path: g.maps.SymbolPath.CIRCLE,
            scale: 8,
            fillColor: '#16a34a',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 3,
          },
        })
      }
    },
    updateLiveMarker(lat, lng) {
      const latitude = parseFloat(lat)
      const longitude = parseFloat(lng)
      if (isNaN(latitude) || isNaN(longitude)) return
      this.liveLocation = { lat: latitude, lng: longitude }
      this.applyLiveMarker()
      if (this._mapObj && this._liveMarker) {
        this._mapObj.panTo(this._liveMarker.getPosition())
      }
    },
    openLightbox(ev) {
      this.lightbox.src = this.evidenceUrl(ev)
      this.lightbox.alt = ev.originalName || 'Evidencia'
      this.lightbox.show = true
    },

    // ── Visor de bitácora (detalle por paso) ──────────────────
    stepPhotos(st) {
      const evs = (st && st.evidences) || []
      // Fotos = evidencias con archivo que no son firma
      return evs.filter(
        e =>
          e.filePath &&
          e.type !== 'firma' &&
          (!e.mimeType || e.mimeType.startsWith('image'))
      )
    },
    stepSignature(st) {
      if (!st) return null
      if (st.signatureData) return st.signatureData
      const sig = ((st.evidences || []).find(e => e.signatureData) || {})
        .signatureData
      return sig || null
    },
    stepGps(st) {
      if (st && st.latitude != null && st.longitude != null)
        return { lat: st.latitude, lng: st.longitude }
      const ev = ((st && st.evidences) || []).find(
        e => e.latitude != null && e.longitude != null
      )
      return ev ? { lat: ev.latitude, lng: ev.longitude } : null
    },
    signatureSrc(data) {
      if (!data) return ''
      return data.startsWith('data:') ? data : `data:image/png;base64,${data}`
    },
    gpsLink(gps) {
      if (!gps) return '#'
      return `https://www.google.com/maps?q=${gps.lat},${gps.lng}`
    },
    openStepDetail(st) {
      this.stepDialog.step = st
      this.stepDialog.show = true
    },
  },

  // ── Socket.IO: tiempo real ──
  sockets: {
    connect() {
      if (this.$socket)
        this.$socket.emit('join_shipment_room', {
          shipmentId: this.$route.params.id,
          token: SessionHelper.token,
        })
    },
    'shipment:status_changed'(data) {
      if (!this.isThisShipment(data)) return
      this.refreshShipment()
      this.$toast.info(`Estado: ${this.statusLabel(data.currentStatus)}`)
    },

    'shipment:assigned'(data) {
      if (!this.isThisShipment(data)) return
      this.refreshShipment()
      this.$toast.info('Repartidor asignado al servicio.')
    },

    'shipment:trip_started'(data) {
      if (!this.isThisShipment(data)) return
      this.liveTracking = true
      if (data.latitude != null && data.longitude != null) {
        this.updateLiveMarker(data.latitude, data.longitude)
      }
      this.refreshShipment()
      this.$toast.success('Viaje iniciado · seguimiento en vivo')
    },

    'shipment:live_location'(data) {
      if (!this.isThisShipment(data)) return
      this.liveTracking = true
      this.updateLiveMarker(data.latitude, data.longitude)
    },

    'shipment:bitacora_step_completed'(data) {
      if (!this.isThisShipment(data)) return
      // Refresca bitácora y estado general (el paso puede mover estado)
      this.loadBitacora()
      this.refreshShipment()
      const title = data.step && data.step.title ? data.step.title : 'Paso'
      this.$toast.info(`Bitácora: ${title} ✓`)
    },

    'shipment:trip_ended'(data) {
      if (!this.isThisShipment(data)) return
      this.liveTracking = false
      this.refreshShipment()
      this.$toast.success('Servicio entregado · viaje finalizado')
    },
  },
}
</script>

<style scoped src="./detail-workspace.css"></style>

<style scoped>
.envio-detail-page {
  background: #f5f6f8;
  min-height: 100vh;
  padding-bottom: 32px;
}

/* ── Hero ── */
.detail-hero {
  padding: 4px 0 8px;
}

.detail-crumb {
  display: flex;
  align-items: center;
  gap: 2px;
  margin: 0 0 6px;
  font-size: 13px;
  color: #6b7280;
}

.detail-crumb__link {
  cursor: pointer;
  color: #4b5d80;
  font-weight: 600;
}

.detail-crumb__link:hover {
  text-decoration: underline;
}

.detail-crumb__sep {
  color: #b4bdc9 !important;
  font-size: 16px !important;
}

.detail-hero__row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.detail-title {
  margin: 0 0 2px;
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.15;
  letter-spacing: -0.01em;
}

.detail-subtitle {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
}

.detail-hero__badges {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  padding-top: 6px;
}

/* ── Loading / error ── */
.detail-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 60px 20px;
  font-size: 14px;
  color: #64748b;
}

.detail-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
  gap: 10px;
}

.detail-error__icon {
  font-size: 44px !important;
  color: #fca5a5 !important;
}
.detail-error__text {
  margin: 0;
  font-size: 14px;
  color: #64748b;
}

/* ── Cards ── */
.detail-card {
  border: 1px solid #e8eaef !important;
  border-radius: 14px !important;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04),
    0 4px 14px rgba(15, 23, 42, 0.04) !important;
  background: #ffffff !important;
  margin-bottom: 0;
  overflow: hidden;
}

.detail-card__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 18px;
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  border-bottom: 1px solid #f0f1f5;
  background: #ffffff;
  letter-spacing: 0.01em;
}

.detail-card__header--origin {
  background: #f7faff;
  border-bottom-color: #e7eef8;
}
.detail-card__header--dest {
  background: #fdf7f7;
  border-bottom-color: #f5e7e7;
}

.detail-card__icon {
  color: #5b7aa3 !important;
  font-size: 17px !important;
  background: rgba(91, 122, 163, 0.1);
  border-radius: 6px;
  padding: 3px;
}
.detail-card__header--dest .detail-card__icon {
  color: #c0494d !important;
  background: rgba(192, 73, 77, 0.1);
}
.detail-card__header--origin .detail-card__icon {
  color: #4b5d80 !important;
  background: rgba(75, 93, 128, 0.1);
}

.ml-auto {
  margin-left: auto !important;
}

.detail-card__body {
  padding: 18px 20px;
}
.detail-card__body--actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.detail-card__body--tracking {
  padding: 0;
}

/* ── Info grid ── */
.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 18px;
}

.info-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.info-field--full {
  grid-column: 1 / -1;
}

.info-label {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.info-value {
  font-size: 13.5px;
  color: #1f2937;
  line-height: 1.45;
}
.info-value--mono {
  font-family: monospace;
  font-size: 13px;
  color: #4b5d80;
}

/* ── Status / priority badges ── */
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 11px;
  border-radius: 20px;
  font-size: 11.5px;
  font-weight: 600;
  white-space: nowrap;
  letter-spacing: 0.01em;
}

.status-badge--pending {
  background: #fdf6e3;
  color: #92711b;
}
.status-badge--assigned {
  background: #e8eef9;
  color: #2f4d7a;
}
.status-badge--transit {
  background: #fbeedb;
  color: #92531c;
}
.status-badge--llegada {
  background: #f3e8ff;
  color: #7e22ce;
}
.status-badge--evidencia {
  background: #fef9c3;
  color: #854d0e;
}
.status-badge--firma {
  background: #e0f2fe;
  color: #0369a1;
}
.status-badge--delivered {
  background: #e3f3e8;
  color: #2a6d3e;
}
.status-badge--default {
  background: #eef0f3;
  color: #5a6678;
}

.priority-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 11px;
  border-radius: 20px;
  font-size: 11.5px;
  font-weight: 600;
  white-space: nowrap;
  letter-spacing: 0.01em;
}

.priority-badge--normal {
  background: #eef0f3;
  color: #5a6678;
}
.priority-badge--high {
  background: #fbeedb;
  color: #92531c;
}
.priority-badge--urgent {
  background: #f7e1e1;
  color: #9e2e2e;
}

/* ── Action buttons ── */
.action-btn {
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  box-shadow: none !important;
  height: 40px !important;
  letter-spacing: 0.01em;
  transition: all 0.15s ease !important;
}

.action-btn--status {
  background: transparent !important;
  color: #e5252a !important;
  border: 1.5px solid #e5252a !important;
  box-shadow: none !important;
}

.action-btn--status:hover {
  background: rgba(229, 37, 42, 0.07) !important;
}

.action-btn--back {
  background: transparent !important;
  color: #6b7280 !important;
  border: 1.5px solid #d1d5db !important;
  box-shadow: none !important;
}

.action-btn--back:hover {
  background: rgba(0, 0, 0, 0.04) !important;
  border-color: #9ca3af !important;
}

/* ── Timeline redesign ── */
.timeline {
  padding: 14px 18px;
}

.timeline-item {
  display: flex;
  gap: 10px;
  position: relative;
}

.timeline-item:last-child .tl-body {
  padding-bottom: 0;
}

.tl-rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  width: 18px;
}

.tl-dot {
  width: 11px;
  height: 11px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 4px;
  border: 2px solid transparent;
}

.tl-line {
  width: 2px;
  flex: 1;
  background: #eef0f3;
  min-height: 16px;
  margin: 4px 0;
}

.tl-body {
  flex: 1;
  min-width: 0;
  padding-bottom: 14px;
}

.tl-transition {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}

.tl-prev {
  font-size: 10.5px;
  color: #9ca3af;
  font-weight: 500;
}

.tl-arrow {
  color: #9ca3af !important;
  font-size: 12px !important;
}

.tl-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 3px;
}

.tl-user {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  color: #4b5563;
  font-weight: 600;
}

.tl-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #e8eef9;
  color: #2f4d7a;
  font-size: 8px;
  font-weight: 700;
  flex-shrink: 0;
}

.tl-date {
  font-size: 10.5px;
  color: #9ca3af;
}

.tl-comment {
  margin: 3px 0 0;
  font-size: 11.5px;
  color: #6b7280;
  line-height: 1.5;
  padding: 4px 8px;
  background: #f8fafc;
  border-left: 2px solid #e2e8f0;
  border-radius: 0 4px 4px 0;
}

/* ── Evidence redesign ── */
.ev-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: #e8eef9;
  color: #2f4d7a;
  font-size: 11px;
  font-weight: 700;
}

.ev-group {
}

.ev-group-label {
  display: flex;
  align-items: center;
  gap: 5px;
  margin: 0 0 10px;
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.ev-group-label .v-icon {
  color: #6b7280 !important;
  font-size: 13px !important;
}

.ev-photo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 8px;
}

.ev-photo-item {
  display: block;
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e8eaef;
  text-decoration: none;
  background: #f8fafc;
  cursor: pointer;
}

.ev-photo-img {
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  display: block;
}

.ev-photo-overlay {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.ev-photo-item:hover .ev-photo-overlay {
  background: rgba(15, 23, 42, 0.48);
}

.ev-photo-foot {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 5px 7px;
  background: #fff;
  border-top: 1px solid #f0f1f5;
}

.ev-photo-foot span {
  font-size: 10px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ev-signature {
  display: flex;
  gap: 0;
  align-items: stretch;
  border: 1px solid #e8eaef;
  border-radius: 10px;
  overflow: hidden;
  background: #fafbfc;
  margin-bottom: 8px;
}

.ev-signature:last-child {
  margin-bottom: 0;
}

.ev-signature-canvas {
  flex-shrink: 0;
  width: 160px;
  background: #fff;
  border-right: 1px solid #f0f1f5;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
}

.ev-signature-img {
  max-width: 100%;
  max-height: 72px;
  object-fit: contain;
}

.ev-signature-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  justify-content: center;
  min-width: 0;
}

.ev-user {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
}

.ev-user .v-icon {
  color: #6b7280 !important;
  font-size: 13px !important;
}
.ev-date {
  font-size: 11px;
  color: #9ca3af;
}
.ev-comment {
  font-size: 12px;
  color: #6b7280;
  font-style: italic;
}

/* ── Empty section ── */
.empty-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  gap: 8px;
}

.empty-section__icon {
  font-size: 36px !important;
  color: #cbd5e1 !important;
}
.empty-section__text {
  margin: 0;
  font-size: 13px;
  color: #94a3b8;
}

/* ── Modal ── */
.modal-card {
  border-radius: 14px !important;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.18) !important;
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 22px;
  background: #ffffff;
  color: #1f2937;
  border-bottom: 1px solid #f0f1f5;
}

.modal-header__icon {
  color: #4b5d80 !important;
  font-size: 20px !important;
  background: rgba(75, 93, 128, 0.1);
  border-radius: 8px;
  padding: 6px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.modal-header__title {
  font-size: 15.5px;
  font-weight: 700;
  color: #1f2937;
  letter-spacing: 0.01em;
}

.modal-body {
  padding: 22px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 22px;
  border-top: 1px solid #f0f1f5;
  background: #fafbfc;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-label {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  letter-spacing: 0.01em;
}

.form-required {
  color: #c0494d;
  font-weight: 700;
}

.btn-cancel {
  border: 1px solid #e5e7eb !important;
  background: #ffffff !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  color: #4b5563 !important;
  height: 38px !important;
  box-shadow: none !important;
}

.btn-cancel:hover {
  background: #f9fafb !important;
  border-color: #d1d5db !important;
}

.btn-save {
  background: #4b5d80 !important;
  color: #ffffff !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  height: 38px !important;
  box-shadow: 0 1px 2px rgba(75, 93, 128, 0.1),
    0 2px 6px rgba(75, 93, 128, 0.18) !important;
  transition: all 0.15s ease !important;
}

.btn-save:hover {
  background: #3a4a68 !important;
  box-shadow: 0 2px 4px rgba(75, 93, 128, 0.14),
    0 4px 10px rgba(75, 93, 128, 0.22) !important;
}

/* ── Asignado (box) ── */
.assignee-box {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f7faff;
  border: 1px solid #e3ebf5;
  border-radius: 12px;
  padding: 10px 12px;
  margin-bottom: 4px;
}
.assignee-box--empty {
  background: #fafbfc;
  border-style: dashed;
  border-color: #e2e8f0;
}
.assignee-box__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #e8eef9;
  flex-shrink: 0;
}
.assignee-box__avatar .v-icon {
  color: #2f4d7a !important;
}
.assignee-box--empty .assignee-box__avatar {
  background: #eef0f3;
}
.assignee-box--empty .assignee-box__avatar .v-icon {
  color: #94a3b8 !important;
}
.assignee-box__info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}
.assignee-box__label {
  font-size: 10.5px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.assignee-box__name {
  font-size: 14px;
  font-weight: 700;
  color: #2f4d7a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.assignee-box__name--muted {
  font-size: 12px;
  font-weight: 500;
  color: #94a3b8;
}
.assignee-box__live {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  font-size: 10.5px;
  font-weight: 800;
  color: #15803d;
  background: #dcfce7;
  border-radius: 20px;
  padding: 3px 9px;
}

/* ── CTA principal ── */
.cta {
  border-radius: 11px !important;
  text-transform: none !important;
  font-weight: 700 !important;
  font-size: 14px !important;
  height: 46px !important;
  letter-spacing: 0.01em;
  margin: 2px 0 4px !important;
  transition: all 0.15s ease !important;
}
.cta--start {
  background: #16a34a !important;
  color: #fff !important;
  box-shadow: 0 6px 16px rgba(22, 163, 74, 0.28) !important;
}
.cta--start:hover {
  background: #15803d !important;
}
.cta--live {
  background: #c79a2b !important;
  color: #fff !important;
  box-shadow: 0 6px 16px rgba(199, 154, 43, 0.28) !important;
}
.cta--live:hover {
  background: #ad841f !important;
}
.cta--assign {
  background: #254f87 !important;
  color: #fff !important;
  box-shadow: 0 6px 16px rgba(37, 79, 135, 0.25) !important;
}
.cta--assign:hover {
  background: #1e3f6d !important;
}
.live-dot--light {
  background: #fff;
  box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.7);
}

/* ── Grid de acciones secundarias ── */
.action-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 2px;
}
.action-grid > div {
  display: flex;
}
.tile {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: 100%;
  padding: 12px 8px;
  border: 1px solid #e8eaef;
  border-radius: 11px;
  background: #fff;
  color: #475569;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.13s ease;
}
.tile:hover {
  border-color: #c7d2e0;
  background: #f8fafc;
  transform: translateY(-1px);
}
.tile:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  transform: none;
}
.tile__icon {
  color: #5b7aa3 !important;
  font-size: 20px !important;
}
.tile--danger {
  color: #b3261e;
}
.tile--danger .tile__icon {
  color: #e5252a !important;
}
.tile--danger:hover {
  border-color: #f7c4c2;
  background: #fff6f5;
}
.tile--start {
  color: #15803d;
  border-color: #bbf7d0;
  background: #f0fdf4;
}
.tile--start .tile__icon {
  color: #16a34a !important;
}
.tile--start:hover {
  border-color: #86efac;
  background: #dcfce7;
}
.tile__live {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #16a34a;
  box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.18);
}

/* ── Volver ── */
.back-link {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  margin-top: 8px;
  padding: 9px;
  border: none;
  background: transparent;
  color: #94a3b8;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 9px;
  transition: all 0.13s ease;
}
.back-link:hover {
  background: #f1f5f9;
  color: #475569;
}

/* ── Action button variants ── */
.action-btn--assign {
  background: transparent !important;
  color: #254f87 !important;
  border: 1.5px solid #254f87 !important;
  box-shadow: none !important;
}

.action-btn--assign:hover {
  background: rgba(37, 79, 135, 0.07) !important;
}

.action-btn--map {
  background: transparent !important;
  color: #c79a2b !important;
  border: 1.5px solid #c79a2b !important;
  box-shadow: none !important;
}

.action-btn--map:hover {
  background: rgba(199, 154, 43, 0.07) !important;
}

.action-btn--tracking {
  background: transparent !important;
  color: #4b5d80 !important;
  border: 1.5px solid #4b5d80 !important;
  box-shadow: none !important;
}

.action-btn--tracking:hover {
  background: rgba(75, 93, 128, 0.07) !important;
}

.action-btn--start {
  background: #16a34a !important;
  color: #fff !important;
  border: none !important;
  box-shadow: 0 4px 12px rgba(22, 163, 74, 0.25) !important;
}
.action-btn--start:hover {
  background: #15803d !important;
}

/* Indicador "en vivo" */
.live-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-left: 8px;
  border-radius: 50%;
  background: #16a34a;
  box-shadow: 0 0 0 0 rgba(22, 163, 74, 0.6);
  animation: live-pulse 1.4s infinite;
}
@keyframes live-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(22, 163, 74, 0.6);
  }
  70% {
    box-shadow: 0 0 0 7px rgba(22, 163, 74, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(22, 163, 74, 0);
  }
}

/* Badge / banner "en vivo" del mapa */
.live-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 10px;
  padding: 3px 10px;
  border-radius: 20px;
  background: #dcfce7;
  color: #15803d;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.05em;
}
.live-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #f0fdf4;
  border-bottom: 1px solid #bbf7d0;
  color: #15803d;
  font-size: 12.5px;
  font-weight: 600;
}
.live-banner__icon {
  color: #16a34a !important;
}

/* ── Bitácora ── */
.bita-progress {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 20px;
  background: #e8eef9;
  color: #2f4d7a;
  font-size: 11.5px;
  font-weight: 700;
}
.bita-timeline {
  display: flex;
  flex-direction: column;
}
.bita-step {
  display: flex;
  gap: 10px;
}
.bita-rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  width: 24px;
}
.bita-dot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #e2e8f0;
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 2px;
}
.bita-dot .v-icon {
  color: #fff !important;
}
.bita-dot--moves {
  outline: 2px solid #f1d27a;
}
.bita-dot--done {
  background: #16a34a;
  color: #fff;
}
.bita-line {
  width: 2px;
  flex: 1;
  background: #e2e8f0;
  margin: 2px 0;
  min-height: 14px;
}
.bita-line--done {
  background: #86efac;
}
.bita-body {
  flex: 1;
  min-width: 0;
  padding-bottom: 14px;
}
.bita-title {
  margin: 2px 0 0;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}
.bita-title--done {
  color: #15803d;
}
.bita-reqs {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 3px;
}
.bita-req {
  color: #94a3b8 !important;
}
.bita-moves {
  font-size: 10.5px;
  font-weight: 700;
  color: #92531c;
  background: #fbeedb;
  padding: 1px 7px;
  border-radius: 10px;
}
.bita-meta {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 4px 0 0;
  font-size: 11px;
  color: #6b7280;
}
.bita-meta .v-icon {
  color: #16a34a !important;
}

/* Chips clickeables del timeline (contenido real capturado) */
.bita-chip {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 7px;
  border: 1px solid #e3ebf5;
  border-radius: 20px;
  background: #f2f6fc;
  color: #2f4d7a;
  font-size: 10.5px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.13s ease;
}
.bita-chip .v-icon {
  color: #2f4d7a !important;
}
.bita-chip--active:hover {
  background: #e3ebf5;
  border-color: #c7d6ea;
}

.bita-comment {
  margin: 6px 0 0;
  font-size: 11.5px;
  font-style: italic;
  color: #64748b;
  line-height: 1.45;
  padding: 5px 9px;
  background: #f8fafc;
  border-left: 2px solid #e2e8f0;
  border-radius: 0 6px 6px 0;
}
.bita-receiver {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 6px 0 0;
  font-size: 11.5px;
  font-weight: 600;
  color: #475569;
}
.bita-receiver .v-icon {
  color: #6b7280 !important;
}

/* ── Visor de paso (step detail dialog) ── */
.step-detail-body {
  padding: 20px 22px !important;
  max-height: 70vh;
  overflow-y: auto;
}
.step-block {
  margin-bottom: 18px;
}
.step-block:last-child {
  margin-bottom: 0;
}
.step-block__label {
  display: flex;
  align-items: center;
  gap: 5px;
  margin: 0 0 8px;
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.step-block__label .v-icon {
  color: #6b7280 !important;
  font-size: 13px !important;
}
.step-block__text {
  margin: 0;
  font-size: 13.5px;
  color: #1f2937;
  line-height: 1.5;
}
.step-signature {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e8eaef;
  border-radius: 10px;
  background: #fafbfc;
  padding: 16px;
}
.step-signature__img {
  max-width: 100%;
  max-height: 180px;
  object-fit: contain;
}
.step-gps-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12.5px;
  font-weight: 600;
  color: #254f87;
  text-decoration: none;
  padding: 6px 12px;
  border: 1px solid #e3ebf5;
  border-radius: 8px;
  background: #f7faff;
}
.step-gps-link:hover {
  background: #e8eef9;
}
.step-gps-link .v-icon {
  color: #254f87 !important;
}

/* ── Lightbox ── */
.lightbox-body {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0f172a;
  padding: 16px;
  min-height: 200px;
}

.lightbox-img {
  max-width: 100%;
  max-height: calc(88vh - 70px);
  object-fit: contain;
  border-radius: 4px;
}

/* ── Assign loading / empty ── */
.assign-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px 0;
  font-size: 13px;
  color: #64748b;
}

.assign-empty {
  font-size: 13px;
  color: #94a3b8;
  margin: 4px 0 0;
}

/* ── Map dialog ── */
.modal-header--map {
  background: #ffffff;
  border-bottom: 1px solid #f0f1f5;
}

.modal-header--map .modal-header__title {
  color: #1f2937;
}
.modal-header--map .modal-header__icon {
  color: #4b5d80 !important;
  background: rgba(75, 93, 128, 0.1);
}

.map-close-btn {
  margin-left: auto !important;
  color: #6b7280 !important;
}

.map-close-btn:hover {
  color: #1f2937 !important;
}

.map-dialog-body {
  position: relative;
  overflow: hidden;
}

.map-legend {
  display: flex;
  gap: 0;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.map-legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  font-size: 12px;
  color: #475569;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.map-legend-item span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.map-legend-item--origin {
  border-right: 1px solid #e2e8f0;
}
.map-legend-item--origin .v-icon {
  color: #254f87 !important;
}
.map-legend-item--dest .v-icon {
  color: #e5252a !important;
}

.map-overlay {
  position: absolute;
  top: 40px; /* below legend */
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  background: rgba(248, 250, 252, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 14px;
  color: #64748b;
  backdrop-filter: blur(2px);
}

.map-error-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #fee2e2;
  color: #b91c1c;
  font-size: 13px;
  border-bottom: 1px solid #fecaca;
}

@media (max-width: 768px) {
  .detail-title {
    font-size: 22px;
  }
  .info-grid {
    grid-template-columns: 1fr;
  }
  .info-field--full {
    grid-column: 1;
  }
  .map-legend {
    flex-direction: column;
  }
  .map-legend-item--origin {
    border-right: none;
    border-bottom: 1px solid #e2e8f0;
  }
}
</style>
